/**
 * FieldNodeDetailPanel — Right-panel detail view for a selected field node.
 *
 * Shows: field name, type, description, PII classification, formula references,
 * enum values, required status, and promoted status.
 *
 * Pattern follows RegulatoryNodeDetailPanel.tsx.
 */

import React from 'react';
import {
  Paper,
  Stack,
  Group,
  Text,
  Badge,
  Divider,
  CloseButton,
  Title,
} from '@mantine/core';

import type { FieldConstellationNode } from './hooks/usePrimitiveConstellation';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface FieldNodeDetailPanelProps {
  /** Selected field node, or null to hide panel. */
  selectedField: FieldConstellationNode | null;
  /** Close handler. */
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function FieldNodeDetailPanel({
  selectedField,
  onClose,
}: FieldNodeDetailPanelProps) {
  if (!selectedField) return null;

  return (
    <Paper p="md" withBorder shadow="sm" w={320}>
      <Stack gap="sm">
        {/* Header */}
        <Group justify="space-between" align="flex-start">
          <Title order={5}>{selectedField.name}</Title>
          <CloseButton onClick={onClose} />
        </Group>

        {/* Badges row */}
        <Group gap="xs">
          <Badge
            variant="filled"
            size="sm"
            color={getFieldTypeColor(selectedField.fieldType)}
          >
            {selectedField.fieldType ?? 'unknown'}
          </Badge>
          {selectedField.required && (
            <Badge variant="filled" size="sm" color="red">
              required
            </Badge>
          )}
          {selectedField.promoted && (
            <Badge variant="filled" size="sm" color="yellow" c="dark">
              promoted
            </Badge>
          )}
          {selectedField.piiClassification &&
            selectedField.piiClassification !== 'none' && (
              <Badge variant="filled" size="sm" color="orange">
                PII: {selectedField.piiClassification}
              </Badge>
            )}
        </Group>

        {/* Description */}
        {selectedField.description && (
          <>
            <Divider />
            <Text size="sm" c="dimmed">
              {selectedField.description}
            </Text>
          </>
        )}

        {/* Enum values */}
        {selectedField.enumValues && selectedField.enumValues.length > 0 && (
          <>
            <Divider />
            <Text size="xs" fw={600}>
              Allowed Values
            </Text>
            <Group gap={4}>
              {selectedField.enumValues.map((val) => (
                <Badge key={val} variant="outline" size="xs">
                  {val}
                </Badge>
              ))}
            </Group>
          </>
        )}

        {/* Formula references */}
        {selectedField.formulaRefs && selectedField.formulaRefs.length > 0 && (
          <>
            <Divider />
            <Text size="xs" fw={600}>
              Formula References
            </Text>
            {selectedField.formulaRefs.map((ref) => (
              <Text key={ref} size="xs" c="dimmed">
                {ref}
              </Text>
            ))}
          </>
        )}

        {/* Metadata */}
        <Divider />
        <Group gap="xs">
          <Text size="xs" c="dimmed">
            Ring:
          </Text>
          <Badge variant="light" size="xs" color={selectedField.color ?? 'blue'}>
            {selectedField.ringId ?? 'unknown'}
          </Badge>
        </Group>
        <Text size="xs" c="dimmed">
          Field: {selectedField.nodeId.replace('field-', '')}
        </Text>
      </Stack>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getFieldTypeColor(fieldType?: string): string {
  switch (fieldType) {
    case 'string':
    case 'text':
      return 'blue';
    case 'number':
    case 'currency':
      return 'green';
    case 'boolean':
      return 'teal';
    case 'datetime':
      return 'grape';
    case 'enum':
      return 'violet';
    case 'json':
      return 'orange';
    case 'reference':
      return 'cyan';
    default:
      return 'gray';
  }
}
